import { ActionsType } from "src/api/sure-api/actions/useGetOneAction";
import { ConstructorOptions, Property, PropertyBuilder, SsqPropertyWithIndexesKey } from "./Builder/PropertyBuilder";
import { ActionScoreByCsr } from "../Actions/useActionScore";
import { ActionData, ActionsCsrMapping } from "../Actions/context";
import { ScoreValue } from "src/api/client-api/post/scoreSchema";
import { ScoreService } from "../EntityBasedScore/ScoreService";

type ExtraPropertyKeys = {
    actionId: number;
    csrThemeId: string;
};

type ActionConstructor = Omit<ConstructorOptions<ExtraPropertyKeys, ScoreValue>, "listOfProperties">;
type ActionDirectorConstructorOptions = ActionConstructor & {
    actions: ActionsType[];
};
export class ActionPropertyBuilder {
    actionPropertyBuilder: PropertyBuilder<ExtraPropertyKeys>;
    _precision: number;

    public constructor(options: ActionDirectorConstructorOptions) {
        const properties = new Array<SsqPropertyWithIndexesKey<ExtraPropertyKeys>>();
        const listOfProperties = options.actions.reduce((acc, action) => {
            action.ssqProperties.forEach((property) => {
                action.csrThemes.forEach((csrTheme) => {
                    const propertyWithActionId = {
                        ...property,
                        extraIndexes: {
                            actionId: action.id,
                            csrThemeId: csrTheme.title,
                        },
                    };
                    acc.push(propertyWithActionId);
                });
            });
            return acc;
        }, properties);

        this._precision = options.precision ?? 0;

        this.actionPropertyBuilder = new PropertyBuilder<ExtraPropertyKeys>({
            listOfProperties: listOfProperties,
            propertiesScore: options.propertiesScore,
            postElementAndCampaign: options.postElementAndCampaign,
            precision: options.precision,
        });

        return this;
    }

    public getProperties = () => {
        const data = this.actionPropertyBuilder.build();
        return data;
    };

    public getPropertiesByCsrThemeAndActions = (options?: { whitelistActionIds?: number[] }): ActionScoreByCsr => {
        const { whitelistActionIds } = options || {};
        const allProperties = this.actionPropertyBuilder.build();

        const allowedPropertyIds = new Set(whitelistActionIds);
        // Ideally, all the bottom would be handled with those simple steps.
        // this.actionPropertyBuilder.buildAgg([
        //     { key: "csrThemeId", op: "add", step: "agg" },
        //     { key: "actionId", op: "add" },
        // ]);

        // Again, the nesting ?!
        // STEP 1-A
        const byCsr: ActionsCsrMapping = {};
        const propertiesByCsr = Object.groupBy(allProperties, (item) => item.extraIndexes.csrThemeId);
        const actionsByCsr = Object.entries(propertiesByCsr).reduce((acc, [csrTheme, propertiesByCsr]) => {
            if (propertiesByCsr === undefined) return acc;

            // Generic aggregations ?
            // Difference... total... whatever.
            // By `type` as input.
            // STEP 1-B
            const totalCsrAcc: { total: number | null; whitelistScore: number | null } = {
                total: null,
                whitelistScore: null,
            };
            const totalCsr = propertiesByCsr.reduce((acc, property) => {
                const localTotal = ScoreService.addScore(acc.total, property.score?.value ?? null);
                acc.total = localTotal;

                if (allowedPropertyIds.has(property.extraIndexes.actionId) === true) {
                    acc.whitelistScore = ScoreService.addScore(acc.whitelistScore, property.score?.value ?? null);
                }
                return acc;
            }, totalCsrAcc);

            // Nested filtered data ?
            // const actData: ActionsDataMapping = {};
            // STEP 2-A
            const byAction: { totalScore: ScoreValue; data: Property[] } = { totalScore: null, data: [] };
            const propertiesByAction = Object.groupBy(propertiesByCsr, (item) => item.extraIndexes.actionId);
            const allActions = Object.entries(propertiesByAction).reduce((acc, [actionId, propertiesByAction]) => {
                if (propertiesByAction === undefined) return acc;

                // Generic aggregation as we've talked above ?!
                // STEP 2-B
                const totalByAction = propertiesByAction.reduce<ScoreValue>((acc, property) => {
                    const localTotal = ScoreService.addScore(acc, property.score?.value ?? null);
                    return localTotal;
                }, null);

                acc.totalScore = totalByAction;
                acc.data = propertiesByAction;
                return acc;
            }, byAction);

            acc[csrTheme] = {
                totalScore: totalCsr.total,
                whitelistScore: totalCsr.whitelistScore,
                data: allActions,
            };
            return acc;
        }, byCsr);

        return actionsByCsr;
    };

    public getPropertiesByActionId = (actionId: number): ActionData => {
        const allProperties = this.actionPropertyBuilder.build();

        const byAction: ActionData = { totalScore: null, data: [] };
        const propertiesByAction = allProperties.reduce((acc, property) => {
            if (property.extraIndexes.actionId !== actionId) return acc;

            const newTotal = ScoreService.addScore(acc.totalScore, property.score?.value ?? null);
            acc.totalScore = newTotal;
            acc.data.push(property);
            return acc;
        }, byAction);

        return propertiesByAction;
    };
}
